import { useState, useEffect, useCallback } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { checkForUpdate, downloadApk, installApk } from "@/lib/appUpdater";

/**
 * Manages the full in-app update lifecycle.
 *
 * States:
 *   idle          – no update available
 *   available     – update found, dialog shown
 *   downloading   – APK download in progress
 *   ready         – download complete, installer launched
 *   error         – something went wrong
 */
export function useAppUpdate() {
  const [state, setState] = useState("idle"); // idle | available | downloading | ready | error
  const [manifest, setManifest] = useState(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [installedVersion, setInstalledVersion] = useState("");

  // Check for an update on mount (Android only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
    let cancelled = false;

    (async () => {
      try {
        const info = await App.getInfo();
        if (!cancelled) setInstalledVersion(info.version);
      } catch { /* ignore */ }

      const update = await checkForUpdate();
      if (!cancelled && update) {
        setManifest(update);
        setState("available");
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const startDownload = useCallback(async () => {
    if (!manifest) return;
    setState("downloading");
    setProgress(0);
    setErrorMsg("");

    try {
      const path = await downloadApk(manifest.apkUrl, (pct) => {
        setProgress(pct);
      });
      // Launch Android package installer
      try {
        await installApk(path);
        setState("ready");
      } catch (err) {
        if (err?.message === "INSTALL_PERMISSION_REQUIRED") {
          // The plugin has already opened the Settings page.
          // Reset to "available" so the user can retry after granting permission.
          setState("available");
          setErrorMsg(
            'Please enable "Install unknown apps" for this app in the settings that just opened, then tap Download again.'
          );
        } else {
          throw err;
        }
      }
    } catch (err) {
      setState("error");
      setErrorMsg(err?.message || "Download failed. Please try again.");
    }
  }, [manifest]);

  const dismiss = useCallback(() => {
    if (manifest?.forceUpdate) return; // cannot dismiss a forced update
    setState("idle");
  }, [manifest]);

  return { state, manifest, progress, errorMsg, installedVersion, startDownload, dismiss };
}
